import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, useDisclosure } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatTile } from '@/components/common/StatTile';
import { useMigrationsList } from '@/features/migrations/hooks';
import { isActive } from '@/features/migrations/status';
import { MigrationsTable } from '@/features/migrations/components/MigrationsTable';
import { MigrationFormModal } from '@/features/migrations/components/MigrationFormModal';
import { MigrationDetailDrawer } from '@/features/migrations/components/MigrationDetailDrawer';
import { ArrowPathIcon, CheckCircleIcon, EnvelopeIcon, ExclamationTriangleIcon, PlusIcon } from '@/icons';

/**
 * Email Migration dashboard: IMAP imports from another provider into Nifty mailboxes.
 * `?mailbox=<id>` scopes the list and preselects the destination; `?job=<id>` opens a job.
 */
export function MigrationsPage() {
  const [params, setParams] = useSearchParams();
  const mailboxId = params.get('mailbox') ?? undefined;
  const openId = params.get('job');
  const { data, isLoading, isError, refetch } = useMigrationsList(mailboxId);
  const form = useDisclosure();

  const jobs = useMemo(() => data ?? [], [data]);
  const stats = useMemo(() => ({
    active: jobs.filter(isActive).length,
    completed: jobs.filter((j) => j.status === 'completed' || j.status === 'completed_with_warnings').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
    copied: jobs.reduce((n, j) => n + j.progress.messagesCopied, 0),
  }), [jobs]);

  const setJob = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('job', id); else next.delete('job');
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader title="Email migration" description="Copy mail, folders and read/flagged state from another provider over IMAP. The old mailbox is not changed."
        actions={<Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={form.onOpen}>New migration</Button>} />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="In progress" value={stats.active} icon={ArrowPathIcon} accent="info" /></div>
        <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Completed" value={stats.completed} icon={CheckCircleIcon} accent="success" /></div>
        <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Failed" value={stats.failed} icon={ExclamationTriangleIcon} accent="danger" /></div>
        <div className="rounded-xl border border-border bg-surface p-5"><StatTile label="Messages copied" value={stats.copied.toLocaleString()} icon={EnvelopeIcon} accent="brand" /></div>
      </div>
      <div className="rounded-xl border border-border bg-surface">
        <MigrationsTable migrations={jobs} isLoading={isLoading} isError={isError} onRetry={() => void refetch()} onOpen={(m) => setJob(m.id)} />
      </div>
      <MigrationFormModal isOpen={form.isOpen} onClose={form.onClose} mailboxId={mailboxId} onCreated={setJob} />
      <MigrationDetailDrawer id={openId} onClose={() => setJob(null)} />
    </div>
  );
}
